import { ReactElement } from 'react';
import { HomepageSlice } from '@adapters/prismic/types/homepage';
import { PageType } from '@adapters/prismic/types/slices';
import HomepageSliceHandler from '../HomepageSliceHandler';
import { AlgonomyPlacement } from '@components/global/Recommendations';
import type { HitProps } from '@components/algolia/Hit';
import type { BundlePromotion } from '@adapters/bigcommerce/transforms/bundlePromotion';
import { sliceBleedAttributes } from '../sliceBleed';
import { renderedSliceTypeIndices } from '../sliceVisibility';

import styles from './HomepageSliceZone.module.scss';

export interface HomepageSliceZoneProps {
  slices: HomepageSlice[];
  pageType: PageType;
  secondSliceZone?: boolean;
  /**
   * The slices rendered above this zone. The homepage splits its slices
   * across two zones, and the analytics index counts across the whole page
   * (ED-592).
   */
  precedingSlices?: HomepageSlice[];
  recommendations?: AlgonomyPlacement[];
  /** ED-345/ED-221/ED-230: SKU->product data resolved in getStaticProps for image_hotspot, image_product_strip and bundle_strip slices. */
  sliceProducts?: Record<string, HitProps>;
  slicePromotions?: Record<number, BundlePromotion>;
}

const HomepageSliceZone = ({
  slices,
  pageType,
  secondSliceZone,
  precedingSlices = [],
  recommendations,
  sliceProducts,
  slicePromotions,
}: HomepageSliceZoneProps): ReactElement => {
  const typeIndices = renderedSliceTypeIndices(
    [...precedingSlices, ...(slices ?? [])],
    { sliceProducts }
  ).slice(precedingSlices.length);

  return (
    <div>
      {slices?.map((slice, i) => {
        const sliceTypeIndex = typeIndices[i];
        return sliceTypeIndex !== null ? (
          <section
            className={styles.homepageSlice}
            key={`homepage-slice-${i}`}
            {...sliceBleedAttributes(slice)}
          >
            <HomepageSliceHandler
              slice={slice}
              pageType={pageType}
              sliceIndex={i}
              sliceTypeIndex={sliceTypeIndex}
              secondSliceZone={secondSliceZone}
              recommendations={recommendations}
              sliceProducts={sliceProducts}
              slicePromotions={slicePromotions}
            />
          </section>
        ) : null;
      })}
    </div>
  );
};

export default HomepageSliceZone;
